import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { useDraggableColumn } from '../../../hooks/dragDrop';
import { useDroppableColumn } from '../../../hooks/dragDrop';
import { useCardHeights } from '../../../hooks/board';
import { useBoardActions, useColumnInfiniteData, useBoardState } from '../../../context/BoardContext';
import { ColumnHeader } from './';
import { ColumnContent } from './';
import { logger } from '../../../utils/logger';
import { useTaskActions } from 'modules/TasksV2/context';

const BoardColumn = ({ column, index, filters = {} }) => {
  const { renameColumn, deleteColumn, moveCard, reorderColumns, duplicateColumn } = useBoardActions();
  const { isRefetchingData } = useBoardState();
  const { openAddTask } = useTaskActions();

  // Use infinite query for this column - automatic pagination management with filters
  const { items, totalCount, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useColumnInfiniteData(
    column.task_stage_id,
    filters
  );

  const prevItemsRef = useRef([]);
  const { listRef, getCardHeight, setCardHeight, resetList } = useCardHeights(column.task_stage_id);

  const { containerRef, handleRef, isDragging, isLocked } = useDraggableColumn({
    columnId: column.task_stage_id,
    index,
    isLocked: column.isLocked,
  });

  // Intercept card drops to Done stage and open dialog
  const handleCardDrop = useCallback(
    (dropData) => {
      const { sourceColumnId, targetColumnId, sourceCardId, targetCardId, edge, taskItem } = dropData;

      // Check if dropping in the same column
      const isSameColumn = sourceColumnId === targetColumnId;

      // Check if dropping in the same position (same column, target is self or adjacent)
      if (isSameColumn) {
        // Find source and target indices in current items
        const sourceIndex = items.findIndex((item) => item.task_id === sourceCardId);
        const targetIndex = items.findIndex((item) => item.task_id === targetCardId);

        if (sourceIndex !== -1 && targetIndex !== -1) {
          // Calculate where the card would be inserted
          let insertPosition = targetIndex;
          if (edge === 'bottom') {
            insertPosition = targetIndex + 1;
          }

          // Check if dropping in the same position or adjacent position
          // Same position: insertPosition === sourceIndex or insertPosition === sourceIndex + 1
          if (insertPosition === sourceIndex || insertPosition === sourceIndex + 1) {
            logger.debug('Card dropped in same position, skipping API call');
            return; // Same position - do nothing
          }
        }
      }

      // Check if dropping to Done stage from a different stage
      const isDoneStage = column.task_stage_name === 'Done';
      const isDifferentStage = sourceColumnId !== targetColumnId;

      if (isDoneStage && isDifferentStage && taskItem) {
        // Only intercept if task is not already completed
        if (taskItem.task_status !== 'completed') {
          return; // Intercept - don't call moveCard
        }
      }

      // Normal drag behavior for other stages or completed tasks
      moveCard(dropData);
    },
    [column.task_stage_name, moveCard, items]
  );

  const { isDraggingOver, edge, type } = useDroppableColumn({
    containerRef,
    columnId: column.task_stage_id,
    index,
    onCardDrop: handleCardDrop,
    onColumnReorder: reorderColumns,
    isLocked: column.isLocked,
  });

  // Reset list when tasks are reordered
  useEffect(() => {
    const prevItems = prevItemsRef.current;
    const itemsChanged = items.length !== prevItems.length || items.some((item, idx) => item.task_id !== prevItems[idx]?.task_id);

    if (itemsChanged && prevItems.length > 0) {
      resetList();
    }

    prevItemsRef.current = items;
  }, [items, resetList]);

  // Memoize derived values
  const showColumnEdge = useMemo(() => type === 'column' && edge, [type, edge]);
  const showCardHighlight = useMemo(() => type === 'card' && isDraggingOver, [type, isDraggingOver]);
  const isDefaultColumn = column?.is_default;

  return (
    <div
      ref={containerRef}
      className={`board-column ${isDragging ? 'column-dragging' : ''}`}
      style={{
        opacity: isDragging ? 0.85 : 1,
        position: 'relative',
      }}
    >
      {/* Drop indicators for column reordering */}
      {showColumnEdge && edge === 'left' && <div className="column-drop-indicator left" />}
      {showColumnEdge && edge === 'right' && <div className="column-drop-indicator right" />}

      {/* Column Header */}
      <ColumnHeader
        column={column}
        itemsCount={items.length}
        totalItems={totalCount}
        isLocked={isLocked}
        handleRef={handleRef}
        isDefaultColumn={isDefaultColumn}
        onRename={renameColumn}
        onDelete={deleteColumn}
        onDuplicate={duplicateColumn}
        openAddTask={openAddTask}
      />

      {/* Column Content with Infinite Scroll */}
      <ColumnContent
        columnId={column.task_stage_id}
        columnStageName={column.task_stage_name}
        items={items}
        isLoading={isLoading}
        isRefetchingData={isRefetchingData}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={fetchNextPage}
        onCardDrop={handleCardDrop}
        getCardHeight={getCardHeight}
        setCardHeight={setCardHeight}
        listRef={listRef}
        showCardHighlight={showCardHighlight}
      />
    </div>
  );
};

// Memoize with custom comparison function
// Note: Return TRUE if props are equal (prevent rerender), FALSE if different (allow rerender)
export default React.memo(BoardColumn, (prevProps, nextProps) => {
  // Check if column props are equal
  if (
    prevProps.column.task_stage_id !== nextProps.column.task_stage_id ||
    prevProps.index !== nextProps.index ||
    prevProps.column.task_stage_name !== nextProps.column.task_stage_name ||
    prevProps.column.isLocked !== nextProps.column.isLocked
  ) {
    return false;
  }

  // Check basic filter props
  if (
    prevProps.filters.searchKeyword !== nextProps.filters.searchKeyword ||
    prevProps.filters.practiceArea !== nextProps.filters.practiceArea ||
    prevProps.filters.sortType !== nextProps.filters.sortType
  ) {
    return false;
  }

  // Check if assignedTo arrays are equal (handle undefined/null cases)
  const prevAssigned = prevProps.filters.assignedTo;
  const nextAssigned = nextProps.filters.assignedTo;

  if (prevAssigned === nextAssigned) {
    return true; // Same reference or both undefined/null
  }

  if (!prevAssigned || !nextAssigned || prevAssigned.length !== nextAssigned.length) {
    return false; // Different if one is null/undefined or different lengths
  }

  // Check array contents
  const assignedToEqual = prevAssigned.every((id, i) => id === nextAssigned[i]);

  return assignedToEqual;
});
